import { API_URL as BASE_URL } from '../../lib/config';
const API_URL = `${BASE_URL}/platform-admin`;

export interface UsageSummary {
  totalOrganizations: number;
  activeOrganizations: number;
  totalUsers: number;
  totalAgents: number;
  last24Hours: {
    apiCalls: number;
    webhooks: number;
    storage: number;
  };
}

export interface OrganizationUsage {
  organizationId: string;
  dateRange: {
    start: string;
    end: string;
  };
  totals: {
    users: number;
    agents: number;
    apiCalls: number;
    webhooks: number;
    storage: number;
    integrations: number;
  };
  dailyMetrics: DailyMetric[];
}

export interface DailyMetric {
  id: string;
  metricDate: string;
  userCount: number;
  agentCount: number;
  apiCallCount: number;
  webhookCount: number;
  storageBytes: string;
  integrationCount: number;
}

export interface TopConsumer {
  organization: {
    id: string;
    name: string;
    status: string;
  };
  apiCalls: number;
  webhooks: number;
  users: number;
  agents: number;
  storage: number;
}

export interface TrendData {
  date: string;
  users: number;
  agents: number;
  apiCalls: number;
  webhooks: number;
  storage: number;
  integrations: number;
}

export const usageAnalyticsService = {
  async getSummary(token: string): Promise<UsageSummary> {
    const response = await fetch(`${API_URL}/usage/summary`, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch usage summary');
    }

    return response.json();
  },

  async getOrganizationUsage(
    token: string,
    organizationId: string,
    startDate?: string,
    endDate?: string
  ): Promise<OrganizationUsage> {
    const queryParams = new URLSearchParams();
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const url = `${API_URL}/usage/organizations/${organizationId}${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch organization usage');
    }

    return response.json();
  },

  async getTopConsumers(token: string, metric?: string): Promise<TopConsumer[]> {
    const queryParams = new URLSearchParams();
    if (metric) queryParams.append('metric', metric);

    const url = `${API_URL}/usage/top-consumers${
      queryParams.toString() ? `?${queryParams.toString()}` : ''
    }`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch top consumers');
    }

    return response.json();
  },

  async getTrends(
    token: string,
    metric: string,
    startDate?: string,
    endDate?: string
  ): Promise<TrendData[]> {
    const queryParams = new URLSearchParams();
    queryParams.append('metric', metric);
    if (startDate) queryParams.append('startDate', startDate);
    if (endDate) queryParams.append('endDate', endDate);

    const url = `${API_URL}/usage/trends?${queryParams.toString()}`;

    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to fetch trends');
    }

    return response.json();
  },
};
